import { useState } from 'react';
import { toast } from '../../utils/toast';
import { extractApiError } from '../../utils/apiError';
import { Button } from '../ui/Button';
import { Avatar } from '../ui/Avatar';
import {
  useCollaboratorSearch,
  hasCollaboratorFilters,
  type UserSearchResult,
} from '../../api/media';
import { useDebouncedValue } from '../media/CollaboratorFilterBar';
import {
  useListEventCollaborators,
  useAddEventCollaborators,
  useRemoveEventCollaborator,
  useResendCollaboratorInvitation,
  type CollaboratorData,
  type CollaboratorEventType,
} from '../../api/events';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../api/api';
import {
  FiX,
  FiUsers,
  FiUserPlus,
  FiMail,
  FiCheck,
  FiAlertCircle,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';

interface ManageCollaboratorsModalProps {
  eventId: string | number;
  eventType: CollaboratorEventType;
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'invite' | 'existing';

export function ManageCollaboratorsModal({
  eventId,
  eventType,
  isOpen,
  onClose,
}: ManageCollaboratorsModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('invite');

  // ── Invite New Tab ──
  const [search, setSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<UserSearchResult[]>([]);
  const [message, setMessage] = useState('');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailInvitees, setEmailInvitees] = useState<string[]>([]);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const { user: currentUser } = useAuth();
  const debouncedSearch = useDebouncedValue(search);
  const searchFilters = { search: debouncedSearch };
  const filtersActive = hasCollaboratorFilters(searchFilters);
  const { data: searchData, isLoading: searchLoading } = useCollaboratorSearch(searchFilters);
  const addCollaborators = useAddEventCollaborators(eventId);

  // ── Existing Tab ──
  // The invitations endpoint is the source of truth for existing collaborators —
  // it returns both accepted and pending entries, so no separate pending-invitations
  // fetch is needed to build the search exclusion list below.
  const {
    data: collaborators,
    isLoading: collabLoading,
    refetch: refetchCollabs,
  } = useListEventCollaborators(eventId, eventType);
  const removeCollaborator = useRemoveEventCollaborator(eventId);
  const resendInvitation = useResendCollaboratorInvitation();
  const [resendingId, setResendingId] = useState<number | null>(null);

  const [removeConfirm, setRemoveConfirm] = useState<{ userId: number } | null>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setSearch('');
    setSelectedUsers([]);
    setMessage('');
    setShowEmailDialog(false);
    setEmailInput('');
    setEmailInvitees([]);
    setShowMore(false);
    setRemoveConfirm(null);
    setActiveTab('invite');
    onClose();
  };

  // Active/valid collaborators (excluding revoked, removed, declined, or cancelled)
  const activeCollaborators = (collaborators ?? []).filter((c) => {
    const s = c.status?.toLowerCase();
    return s !== 'revoked' && s !== 'removed' && s !== 'declined' && s !== 'cancelled';
  });

  // ── Search results ──
  // Exclude users who are already active collaborators (accepted or pending), or are the host/current user
  const existingUserIds = new Set<number | string>();
  if (currentUser?.id != null) {
    existingUserIds.add(currentUser.id);
    existingUserIds.add(Number(currentUser.id));
  }
  activeCollaborators.forEach((c) => {
    if (c.user_id != null) {
      existingUserIds.add(c.user_id);
      existingUserIds.add(Number(c.user_id));
    }
  });

  const rawUsers = filtersActive
    ? (searchData?.pages?.flatMap((p: any) => p.results ?? []) ?? [])
    : [];
  const allUsers = rawUsers.filter(
    (u: UserSearchResult) => !existingUserIds.has(u.id) && !existingUserIds.has(Number(u.id)),
  );
  const displayUsers = showMore ? allUsers : allUsers.slice(0, 5);

  const toggleUser = (user: UserSearchResult) => {
    setSelectedUsers((prev) => {
      const exists = prev.some((u) => u.id === user.id);
      if (exists) return prev.filter((u) => u.id !== user.id);
      return [...prev, user];
    });
  };

  const handleSendInvitations = () => {
    if (selectedUsers.length === 0) return;
    addCollaborators.mutate(
      {
        user_ids: selectedUsers.map((u) => u.id),
        event_type: eventType,
        message: message || undefined,
      },
      {
        onSuccess: (data) => {
          const results = data?.results;
          const successful = results?.successful?.length ?? selectedUsers.length;
          toast.success(`${successful} collaborator(s) invited`);
          setSelectedUsers([]);
          setMessage('');
          refetchCollabs();
        },
        onError: (err: any) => {
          toast.error(extractApiError(err, 'Failed to send invitations'));
        },
      },
    );
  };

  const handleEmailInvite = async () => {
    let finalInvitees = [...emailInvitees];
    const trimmed = emailInput.trim().replace(/,/g, '');
    if (trimmed && trimmed.includes('@') && !finalInvitees.includes(trimmed)) {
      if (currentUser && trimmed.toLowerCase() === currentUser.email?.toLowerCase()) {
        toast.error('You cannot invite yourself.');
      } else {
        finalInvitees.push(trimmed);
      }
    }
    if (currentUser) {
      const selfEmail = currentUser.email?.toLowerCase();
      if (finalInvitees.some((email) => email.toLowerCase() === selfEmail)) {
        toast.error('You cannot invite yourself.');
        finalInvitees = finalInvitees.filter((email) => email.toLowerCase() !== selfEmail);
      }
    }
    if (finalInvitees.length === 0) return;
    try {
      const formData = new FormData();
      formData.append('invitees', JSON.stringify(finalInvitees));
      await apiClient.post(`/event/${eventId}/collaborators/invite/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${finalInvitees.length} email invite(s) sent`);
      setEmailInvitees([]);
      setEmailInput('');
      setShowEmailDialog(false);
      refetchCollabs();
    } catch (err: any) {
      toast.error(extractApiError(err, 'Failed to send email invitations'));
    }
  };

  const handleRemoveCollaborator = (userId: number) => {
    removeCollaborator.mutate(
      { user_id: userId, event_type: eventType },
      {
        onSuccess: () => {
          toast.success('Collaborator removed');
          setRemoveConfirm(null);
          refetchCollabs();
        },
        onError: () => {
          toast.error('Failed to remove collaborator');
          setRemoveConfirm(null);
        },
      },
    );
  };

  const handleResendInvitation = (invitationId: number) => {
    setResendingId(invitationId);
    resendInvitation.mutate(invitationId, {
      onSuccess: () => {
        toast.success('Invitation resent');
        setResendingId(null);
      },
      onError: () => {
        toast.error('Failed to resend invitation');
        setResendingId(null);
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:px-4 sm:py-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative w-full sm:max-w-lg h-[80dvh] sm:h-[540px] max-h-[85dvh] bg-zinc-950 border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-white/8 shrink-0">
          <h2 className="text-white text-lg font-semibold">Manage Collaborators</h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/8 text-zinc-400 hover:text-white transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/8 shrink-0">
          <button
            onClick={() => setActiveTab('invite')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-[14px] font-medium transition-colors ${
              activeTab === 'invite'
                ? 'text-white border-b-2 border-blue-500'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <FiUserPlus size={14} />
            Invite New
          </button>
          <button
            onClick={() => setActiveTab('existing')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-[14px] font-medium transition-colors ${
              activeTab === 'existing'
                ? 'text-white border-b-2 border-blue-500'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <FiUsers size={14} />
            Existing
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
          {/* ── Invite New Tab ── */}
          {activeTab === 'invite' && (
            <>
              {/* Search */}
              <div className="relative">
                <FiSearch
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search users by name or username"
                  className="w-full pl-10 pr-4 py-3 rounded-2xl bg-zinc-900 border border-white/10 text-white placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/60 text-[14px]"
                />
              </div>

              {/* Selected users chips */}
              {selectedUsers.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map((u) => (
                    <span
                      key={u.id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-600/20 border border-blue-500/40 text-blue-300 text-sm"
                    >
                      <Avatar
                        src={u.prof_pics}
                        alt={u.username}
                        name={u.full_name || u.username}
                        size="xs"
                      />
                      {u.full_name?.split(' ')[0] ?? u.username}
                      <button onClick={() => toggleUser(u)} className="hover:text-white">
                        <FiX size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Email invite button */}
              <button
                onClick={() => setShowEmailDialog(true)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white transition-all text-[14px]"
              >
                <FiMail size={14} />
                Invite via Email
              </button>

              {/* Optional message */}
              <div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a personal message (optional)"
                  rows={2}
                  className="w-full bg-zinc-900 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/60 text-[14px] resize-none"
                />
              </div>

              {/* User results */}
              {searchLoading && (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                </div>
              )}

              {!searchLoading && displayUsers.length > 0 && (
                <div className="space-y-1">
                  {displayUsers.map((u) => {
                    const isSelected = selectedUsers.some((s) => s.id === u.id);
                    return (
                      <button
                        key={u.id}
                        onClick={() => toggleUser(u)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors text-left"
                      >
                        <Avatar
                          src={u.prof_pics}
                          alt={u.username}
                          name={u.full_name || u.username}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-[14px] truncate">
                            {u.full_name || u.username}
                          </p>
                          <p className="text-zinc-500 text-[12px] truncate">@{u.username}</p>
                        </div>
                        <div
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                            isSelected ? 'bg-blue-500 border-blue-500' : 'border-zinc-600'
                          }`}
                        >
                          {isSelected && <FiCheck size={12} className="text-white" />}
                        </div>
                      </button>
                    );
                  })}
                  {allUsers.length > 5 && (
                    <button
                      onClick={() => setShowMore((p) => !p)}
                      className="w-full text-center text-blue-400 text-[13px] py-2 hover:text-blue-300"
                    >
                      {showMore ? (
                        <span className="flex items-center justify-center gap-1">
                          View Less <FiChevronUp size={14} />
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-1">
                          View More ({allUsers.length - 5}+) <FiChevronDown size={14} />
                        </span>
                      )}
                    </button>
                  )}
                </div>
              )}

              {!searchLoading && displayUsers.length === 0 && (
                <p className="text-zinc-500 text-sm text-center py-8">
                  {filtersActive
                    ? 'No users found'
                    : 'Search by name or username to find collaborators'}
                </p>
              )}

              {/* Send button */}
              {selectedUsers.length > 0 && (
                <Button
                  onClick={handleSendInvitations}
                  disabled={addCollaborators.isPending}
                  className="w-full rounded-full"
                  size="lg"
                >
                  {addCollaborators.isPending
                    ? 'Sending…'
                    : `Send ${selectedUsers.length} Invitation(s)`}
                </Button>
              )}

              {/* Email Invite Dialog */}
              {showEmailDialog && (
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center rounded-3xl z-10">
                  <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6 mx-4 max-w-sm w-full shadow-2xl">
                    <h3 className="text-white font-semibold mb-3">Invite via Email</h3>
                    <div
                      className={`bg-zinc-800 border rounded-xl px-4 py-3 min-h-[80px] mb-3 transition-colors ${
                        isEmailFocused
                          ? 'border-blue-500/60 ring-1 ring-blue-500/30'
                          : 'border-white/8'
                      }`}
                    >
                      {emailInvitees.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {emailInvitees.map((e, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-600/20 border border-blue-500/40 text-blue-300 text-sm"
                            >
                              {e}
                              <button
                                onClick={() =>
                                  setEmailInvitees((prev) => prev.filter((_, idx) => idx !== i))
                                }
                                className="hover:text-white"
                              >
                                <FiX size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <input
                        type="email"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onFocus={() => setIsEmailFocused(true)}
                        onBlur={() => {
                          setIsEmailFocused(false);
                          const trimmed = emailInput.trim().replace(/,/g, '');
                          if (
                            trimmed &&
                            trimmed.includes('@') &&
                            !emailInvitees.includes(trimmed)
                          ) {
                            if (
                              currentUser &&
                              trimmed.toLowerCase() === currentUser.email?.toLowerCase()
                            ) {
                              toast.error('You cannot invite yourself.');
                              return;
                            }
                            setEmailInvitees((prev) => [...prev, trimmed]);
                            setEmailInput('');
                          }
                        }}
                        onKeyDown={(e) => {
                          if (
                            (e.key === 'Enter' || e.key === ',' || e.key === ' ') &&
                            emailInput.trim()
                          ) {
                            e.preventDefault();
                            const trimmed = emailInput.trim().replace(/,/g, '');
                            if (trimmed.includes('@') && !emailInvitees.includes(trimmed)) {
                              if (
                                currentUser &&
                                trimmed.toLowerCase() === currentUser.email?.toLowerCase()
                              ) {
                                toast.error('You cannot invite yourself.');
                                return;
                              }
                              setEmailInvitees((prev) => [...prev, trimmed]);
                              setEmailInput('');
                            }
                          }
                        }}
                        placeholder="email@example.com"
                        className="w-full bg-transparent border-0 outline-none ring-0 text-white placeholder:text-zinc-600 focus:ring-0 focus:outline-none focus-visible:outline-none shadow-none text-[14px]"
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowEmailDialog(false);
                          setEmailInvitees([]);
                          setEmailInput('');
                        }}
                        className="flex-1 py-3 rounded-full border border-white/10 text-zinc-300 text-[14px] font-medium hover:bg-white/5"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleEmailInvite}
                        disabled={emailInvitees.length === 0 && !emailInput.trim().includes('@')}
                        className="flex-1 py-3 rounded-full bg-blue-600 text-white text-[14px] font-medium hover:bg-blue-500 disabled:opacity-40"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Existing Tab ── */}
          {activeTab === 'existing' && (
            <>
              {/* Collaborator List */}
              <div className="space-y-3">
                <p className="text-white text-sm font-medium">
                  Collaborators ({activeCollaborators.length})
                </p>

                {collabLoading && (
                  <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  </div>
                )}

                {!collabLoading && activeCollaborators.length === 0 && (
                  <p className="text-zinc-500 text-sm text-center py-8">No collaborators yet</p>
                )}

                {!collabLoading &&
                  activeCollaborators.map((c: CollaboratorData) => {
                    const displayName = c.invitee_name || c.invitee_email || 'Invited User';
                    const isPending = c.status?.toLowerCase() === 'pending';
                    const statusLabel = c.status
                      ? c.status.charAt(0).toUpperCase() + c.status.slice(1).toLowerCase()
                      : '';

                    return (
                      <div
                        key={c.id}
                        className="flex items-center justify-between p-3 rounded-xl bg-zinc-900 border border-white/8"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar alt={displayName} name={displayName} size="sm" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <p className="text-white text-[14px] truncate min-w-0">
                                {displayName}
                              </p>
                              {statusLabel && (
                                <span
                                  className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${
                                    isPending
                                      ? 'bg-amber-500/10 text-amber-400'
                                      : 'bg-emerald-500/10 text-emerald-400'
                                  }`}
                                >
                                  {statusLabel}
                                </span>
                              )}
                            </div>
                            {c.invitee_email && c.invitee_name && (
                              <p className="text-zinc-500 text-[12px] truncate">
                                {c.invitee_email}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-3">
                          {isPending && (
                            <button
                              onClick={() => handleResendInvitation(c.id)}
                              disabled={resendingId === c.id}
                              className="text-[12px] text-blue-400 hover:text-blue-300 font-medium px-3 py-1.5 rounded-full hover:bg-blue-500/10 transition-colors disabled:opacity-40"
                            >
                              {resendingId === c.id ? 'Resending…' : 'Resend'}
                            </button>
                          )}
                          {c.user_id != null && (
                            <button
                              onClick={() => setRemoveConfirm({ userId: c.user_id })}
                              className="text-[12px] text-red-400 hover:text-red-300 font-medium px-3 py-1.5 rounded-full hover:bg-red-500/10 transition-colors"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>

        {/* Remove Confirmation Dialog */}
        {removeConfirm && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center rounded-3xl z-10">
            <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6 mx-4 max-w-xs w-full shadow-2xl">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center">
                  <FiAlertCircle size={24} className="text-red-400" />
                </div>
              </div>
              <p className="text-white text-center font-medium mb-1">Remove Collaborator?</p>
              <p className="text-zinc-500 text-sm text-center mb-5">
                This person will no longer have access to manage this event.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setRemoveConfirm(null)}
                  className="flex-1 py-3 rounded-full border border-white/10 text-zinc-300 text-[14px] font-medium hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRemoveCollaborator(removeConfirm.userId)}
                  disabled={removeCollaborator.isPending}
                  className="flex-1 py-3 rounded-full bg-red-600 text-white text-[14px] font-medium hover:bg-red-500 disabled:opacity-40"
                >
                  {removeCollaborator.isPending ? 'Removing…' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="shrink-0 pb-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}
